import { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { riskColorMapping, SolidGaugeChart } from './ClimateCharts';
import ProjectOverview from '../../resources/images/Project_overview.png';
import EnvPara from '../../resources/images/envparameter.png';
import { ProjectInfoCard } from '../components/cardComponent';
import { useAppState } from '../../state-management/state-provider';
import SiteMapWithLayer from '../DMap/SiteMapWithLayer';
import SiteList from '../components/SiteList';
import {
  extractRiskIncreaseLines,
  isSuccessEnvAnalysis,
  roundToTwoDecimalPlaces,
} from 'src/util/util';
import { EnvironmentalRiskCategory, SampleProjectId } from 'src/enum';
import { EnvironmentalSiteAnalysis, Site, UserRole } from 'src/api/Api';
import { ChartComponent } from '../components/chartComponent';
import { extractHeatmapData, extractSectorList } from 'src/util/util';
import { Heatmap } from './HeatMapChart';
import { LineChart } from './LineChart';
import Highcharts from 'highcharts';

const EnvDashboard = () => {
  const { state } = useAppState();
  const { selectedProject, user } = state;
  const { environmentalSiteAnalysis, projectDetails, environmentalProjectAnalysis } =
    selectedProject;
  const [selectedSectorProductivity, setSelectedSectorProductivity] = useState<string>('');
  const [selectedSectorDamage, setSelectedSectorDamage] = useState<string>('');
  const [selectedSectorLine, setSelectedSectorLine] = useState<string>('');
  const [sectors, setSectors] = useState<string[]>([]);

  
      const isUserRole = useMemo(() => {
        if (!user?.role || user.role.length === 0) return false;
        // Show only if user has ONLY User role (no Admin or Employee)
        const roles = user.role;
        const hasUserRole = roles.includes(UserRole.User);
        const hasAdminRole = roles.includes(UserRole.Admin);
        const hasEmployeeRole = roles.includes(UserRole.Employee);
        // Only show if user has User role and doesn't have Admin or Employee
        return hasUserRole && !hasAdminRole && !hasEmployeeRole;
      }, [user]);
      const isSampleProject = projectDetails?.project_id === SampleProjectId;
      // Disable exporting when project is the sample project AND the current user
      // has only the "User" role. Otherwise allow exporting.
      const allowExporting = !(isSampleProject && isUserRole);
      console.log('Is User Role:', isUserRole, allowExporting);
      // Ensure Highcharts global exporting option follows our allowExporting flag
      useEffect(() => {
        try {
          Highcharts.setOptions({
            exporting: {
              enabled: allowExporting,
            },
          });
        } catch (e) {
          // Ignore if Highcharts isn't ready; this is defensive
          console.warn('Failed to set Highcharts exporting option', e);
        }
      }, [allowExporting]);
  
  useEffect(() => {
    if (!environmentalProjectAnalysis?.chronic_risks) return;

    const scenarios = environmentalProjectAnalysis.chronic_risks.risk_scenarios;
    const sectorNames = extractSectorList(scenarios);

    setSectors(sectorNames);

    // Set defaults only once when data loads
    if (sectorNames.length > 0) {
      setSelectedSectorProductivity(sectorNames[0]);
      setSelectedSectorDamage(sectorNames[0]);
      setSelectedSectorLine(sectorNames[0]);
    }
  }, [environmentalProjectAnalysis]);

  // ----------------------------------------
  // PIE CHART DATA: RISK CATEGORY PERCENTAGES
  // ----------------------------------------

  const pieData = (() => {
    if (!projectDetails?.sites) return [];

    const counts = {} as Record<string, number>;

    environmentalSiteAnalysis?.forEach((s) => {
      const cat = s.site_risk_category || 'Unknown';
      counts[cat] = (counts[cat] || 0) + 1;
    });

    const total = projectDetails.sites.length;

    return Object.entries(counts).map(([name, count]) => ({
      name,
      value1: parseFloat(((count / total) * 100).toFixed(2)), // percent
    }));
  })();
  return (
    <Box sx={{ mt: 5 }}>
      {projectDetails?.sites?.length && (
        <Box className="bio-map" mb={3} height={600}>
          <SiteMapWithLayer
            sites={projectDetails?.sites?.filter(isSuccessEnvAnalysis)}
            requiredBounrdaryMarker={true}
            fitToBoundary={true}
          ></SiteMapWithLayer>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          border: '1px solid #ccc',
          backgroundColor: '#fff',
          margin: '10px 1px',
          borderRadius: '8px',
          padding: 2, // Added padding for content spacing
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '22px', color: '#151E15', marginBottom: 2 }}>
          {projectDetails?.project_name}
        </Typography>
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #D9D9D9' }}
          gap={2}
        />
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            padding: 2,
            border: '1px solid #E0E0E0',
            backgroundColor: '#FAFAFA',
            borderRadius: '10px',
          }}
        >
          {/* LEFT SUMMARY CARD */}
          <Grid size={2} sx={{ flexShrink: 0 }}>
            <ProjectInfoCard
              imageSrc={ProjectOverview}
              title="Summary"
              data={[
                {
                  label: 'Area',
                  value: `${String(
                    roundToTwoDecimalPlaces(
                      projectDetails?.total_area ||
                        projectDetails?.sites?.reduce(
                          (a: number, i: Site) => a + (i.area ? i.area : 0),
                          0,
                        ),
                    ),
                  )} ${projectDetails?.area_unit}`,
                },
                { label: 'Sites', value: String(projectDetails?.sites?.length) ?? 0 },
                {
                  label: 'Value at Risk',
                  value: environmentalProjectAnalysis?.value_at_risk
                    ? `${environmentalProjectAnalysis?.value_at_risk} %`
                    : null,
                  color:
                    riskColorMapping[
                      environmentalProjectAnalysis?.overall_risk_category as EnvironmentalRiskCategory
                    ],
                },
              ]}
            />
          </Grid>

          {/* RIGHT — MULTIPLE SITE GAUGE DIALS */}
          <Grid
            sx={{
              flexGrow: 1,
              padding: 2,
              borderRadius: '12px',
              backgroundColor: '#FFFFFF',
              boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
              <Box
                component="img"
                src={EnvPara}
                alt="Project Overview"
                sx={{ width: '45px', height: '35px', objectFit: 'contain' }}
              />
              <Typography sx={{ fontWeight: 600, fontSize: '18px', padding: '10px 0' }}>
                Site Risk Category
              </Typography>
            </Box>

            <Box
              sx={{
                mt: 2,
                width: '100%',
                maxHeight: '250px',
                overflowY:
                  environmentalSiteAnalysis && environmentalSiteAnalysis.length > 3
                    ? 'auto'
                    : 'visible',

                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-evenly',
                gap: 2,
                px: 1,
                pb: 1,

                // Scrollbar styling
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#BDBDBD',
                  borderRadius: '6px',
                },
              }}
            >
              {environmentalSiteAnalysis?.map((site: EnvironmentalSiteAnalysis, index) => {
                const siteDetail = projectDetails?.sites?.find(
                  (i: Site) => i.site_id === site.site_id,
                );
                if (!siteDetail) return null;

                return (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      backgroundColor: '#F9F9F9',
                      boxShadow: '0px 1px 4px rgba(0,0,0,0.05)',
                      textAlign: 'center',

                      /** Card dimensions */
                      minHeight: '230px',
                      flex: '1 1 calc(33.33% - 16px)', // max 3 per row
                      maxWidth: 'calc(33.33% - 16px)',

                      /** Responsive breakpoints */
                      '@media (max-width: 1200px)': {
                        flex: '1 1 calc(50% - 16px)', // 2 per row
                        maxWidth: 'calc(50% - 16px)',
                      },
                      '@media (max-width: 700px)': {
                        flex: '1 1 100%', // 1 per row
                        maxWidth: '100%',
                      },

                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      sx={{ fontWeight: 600, fontSize: '16px', mb: 1, color: '#151E15' }}
                    >
                      {siteDetail.site_name}
                    </Typography>

                    <SolidGaugeChart
                      title=""
                      category={site.site_risk_category as EnvironmentalRiskCategory}
                      riskLevelTitle={site.site_risk_category}
                    />
                  </Box>
                );
              })}
            </Box>
          </Grid>
        </Box>

        {/* PIE CHART */}
        <Grid sx={{ flexGrow: 1, padding: 1 }}>
          <ChartComponent
            chartType="pie"
            chartTitle="Site Risk Category Distribution"
            seriesValue1="Percentage"
            data={pieData}
            export={allowExporting}
          />
        </Grid>
        <Grid sx={{ flexGrow: 1, padding: 1 }}>
          {environmentalProjectAnalysis?.chronic_risks && (
            <Box sx={{ mt: 3, p: 2 }}>
              {/* ----------------------------- */}
              {/* ROW 1 — PRODUCTIVITY */}
              {/* ----------------------------- */}
              <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
                Productivity Value at Risk (%)
              </Typography>

              <Typography sx={{ fontWeight: 500, mt: 1 }}>
                Select Asset Type (Productivity)
              </Typography>

              <select
                value={selectedSectorProductivity}
                onChange={(e) => setSelectedSectorProductivity(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  marginTop: '6px',
                }}
              >
                {sectors.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              {/* Productivity Heatmaps */}
              <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                {environmentalProjectAnalysis.chronic_risks.risk_scenarios.map((scenario, idx) => {
                  const { years, hazards, matrix } = extractHeatmapData(
                    scenario,
                    selectedSectorProductivity,
                    'productivity_var_percent',
                  );

                  return (
                    <Box key={idx} sx={{ width: '33%' }}>
                      <Heatmap
                        title={`${scenario.scenario} — ${selectedSectorProductivity}`}
                        years={years}
                        hazards={hazards}
                        data={matrix}
                        exporting={allowExporting}
                      />
                    </Box>
                  );
                })}
              </Box>
              {/* ----------------------------- */}
              {/* ROW 2 — ASSET DAMAGE */}
              {/* ----------------------------- */}
              <Typography sx={{ fontWeight: 600, fontSize: '18px', mt: 5 }}>
                Asset Damage Value at Risk (%)
              </Typography>

              <Typography sx={{ fontWeight: 500, mt: 1 }}>Select Asset Type (Damage)</Typography>

              <select
                value={selectedSectorDamage}
                onChange={(e) => setSelectedSectorDamage(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  marginTop: '6px',
                }}
              >
                {sectors.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              {/* Damage Heatmaps */}
              <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                {environmentalProjectAnalysis.chronic_risks.risk_scenarios.map((scenario, idx) => {
                  const { years, hazards, matrix } = extractHeatmapData(
                    scenario,
                    selectedSectorDamage,
                    'asset_damage_var_percent',
                  );

                  return (
                    <Box key={idx} sx={{ width: '33%' }}>
                      <Heatmap
                        title={`${scenario.scenario} — ${selectedSectorDamage} — Damage`}
                        years={years}
                        hazards={hazards}
                        data={matrix}
                        exporting={allowExporting}
                      />
                    </Box>
                  );
                })}
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: '18px', mt: 6 }}>
                Risk Increase Percent (Line Chart)
              </Typography>
              <select
                value={selectedSectorLine}
                onChange={(e) => setSelectedSectorLine(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  marginTop: '6px',
                }}
              >
                {sectors.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                {environmentalProjectAnalysis.chronic_risks.risk_scenarios.map((scenario, idx) => {
                  const { years, series } = extractRiskIncreaseLines(scenario, selectedSectorLine);

                  return (
                    <Box key={idx} sx={{ width: '33%' }}>
                      <LineChart
                        title={`${scenario.scenario} — ${selectedSectorDamage}`}
                        years={years}
                        series={series}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Grid>
      </Box>
      <SiteList type="climate"></SiteList>
    </Box>
  );
};

export default EnvDashboard;
